import { AkaCartAdviceValues } from "Components/Features/CheckPrice/Products/Advice/ProductDetails/FormElements/interfaces";
import { useExistingCart } from "Components/Features/CheckPrice/hooks/checkPriceHooks";
import { useTexts } from "Components/TextContext";
import { isAdviceProduct } from "Store/advice/utils";
import {
  AdviceVersion,
  useExistingAdviceVersion,
} from "Store/akaAdvice/akaAdviceHooks";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products";
import { InsuranceProduct } from "Store/interfaces/products/checkout";
import { useOfferDocumentLink } from "Store/offerAccept/offerAcceptHooks";
import { isDefined } from "Utils/functions";
import React, { useEffect, useRef, useState } from "react";
import { AdviceLink } from "../CardFooter/styled";
import {
  ChipStyled,
  CustomerRecommendation,
  CustomerRecommendationText,
  ExpandRecommendationButton,
  StyledDesc,
} from "./styled";

export const CustomerRecommendationMessage = ({
  product,
}: {
  readonly product: InsuranceProduct;
}): React.ReactElement | null => {
  const { getText } = useTexts();

  const cartEntity = useExistingCart();
  const existingAdviceVersion = useExistingAdviceVersion();
  const isAdvice = isAdviceProduct(product.name);
  const offerDocumentLink = useOfferDocumentLink();
  const [messageIsMoreThanThreeLines, setMessageIsMoreThanThreeLines] =
    useState(false);
  const [recommendationMessageIsExpanded, setRecommendationMessageIsExpanded] =
    useState(false);
  const recommendationMessageRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    const checkIfContentExceedsThreeLines = (): boolean => {
      if (recommendationMessageRef.current) {
        const element = recommendationMessageRef.current;
        const scrollHeight = element.scrollHeight;
        const lineHeight = parseFloat(getComputedStyle(element).lineHeight);
        const maxHeight = lineHeight * 3;
        return scrollHeight > maxHeight;
      }
      return false;
    };

    setMessageIsMoreThanThreeLines(checkIfContentExceedsThreeLines());
  }, [product.customerRecommendation]);

  if (isAdvice && existingAdviceVersion === AdviceVersion.NEW) {
    const newAdvice = cartEntity.current?.advice as AkaCartAdviceValues;

    if (showAdviceCustomerRecommendationMessage(product, newAdvice)) {
      return (
        <StyledDesc>
          {getText("offer.description.isadviceproducttext")}{" "}
          <AdviceLink href={offerDocumentLink} target="_blank" rel="noreferrer">
            {getText("offer.description.isadviceproduct")}
          </AdviceLink>
          .
        </StyledDesc>
      );
    }
  }

  if (!isDefined(product?.customerRecommendation)) {
    return null;
  }

  return (
    <CustomerRecommendation>
      <ChipStyled>
        {getText("offer.description.customerrecommendation")}
      </ChipStyled>
      <CustomerRecommendationText
        ref={recommendationMessageRef}
        isExpanded={recommendationMessageIsExpanded}
      >
        {product.customerRecommendation}
      </CustomerRecommendationText>
      {messageIsMoreThanThreeLines && (
        <ExpandRecommendationButton
          onClick={() =>
            setRecommendationMessageIsExpanded(!recommendationMessageIsExpanded)
          }
        >
          {recommendationMessageIsExpanded
            ? getText("offer.description.customerrecommendationreadless")
            : getText("offer.description.customerrecommendationreadmore")}
        </ExpandRecommendationButton>
      )}
    </CustomerRecommendation>
  );
};

const showAdviceCustomerRecommendationMessage = (
  product: InsuranceProduct,
  advice: AkaCartAdviceValues,
): boolean => {
  const finalAdviceData = advice.finalAkaAdviceData;
  switch (product.name) {
    case INSURANCE_NAME_VALUE.CRITICAL_ILLNESS:
      return !!finalAdviceData.criticalIllnessAdviceComment;
    case INSURANCE_NAME_VALUE.DISABILITY_FLEX:
      return !!finalAdviceData.disabilityAdviceComment;
    case INSURANCE_NAME_VALUE.DEATH:
      return !!finalAdviceData.deathAdviceComment;
    default:
      return false;
  }
};
